// components/website/cookie-banner.tsx
"use client";

import { useState, useSyncExternalStore } from "react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import {
  readConsentFromDocument,
  writeConsentToDocument,
  type ConsentLevel,
} from "@/lib/consent";

/**
 * Cookie consent banner — three explicit choices, no dark patterns.
 *
 * Rendered globally on the public site (via the (public) and (website)
 * layouts). Stays hidden if the visitor has already chosen. Records
 * the choice via `fm-consent` cookie (12-month TTL) and emits a
 * window event so analytics-mount components can react without
 * waiting for a full reload.
 */
export function CookieBanner() {
  const t = useTranslations("website.cookies");
  const [busy, setBusy] = useState(false);
  const visible = useSyncExternalStore(
    (onStoreChange) => {
      if (typeof window === "undefined") return () => {};
      const handler = () => onStoreChange();
      window.addEventListener("fm-consent-changed", handler);
      return () => window.removeEventListener("fm-consent-changed", handler);
    },
    () => readConsentFromDocument() === null,
    () => false
  );

  function decide(level: ConsentLevel) {
    if (busy) return;
    setBusy(true);
    writeConsentToDocument(level);
    // Let analytics-mount components react without a hard reload.
    window.dispatchEvent(new CustomEvent("fm-consent-changed", { detail: level }));
    setBusy(false);
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-body"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-(--fm-border) bg-(--fm-surface) shadow-2xl"
    >
      <div className="mx-auto max-w-7xl px-6 py-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-(--fm-text-primary)">
          <p id="cookie-banner-title" className="font-semibold">
            {t("title")}
          </p>
          <p id="cookie-banner-body" className="mt-1 text-(--fm-text-secondary)">
            {t("body")}{" "}
            <Link
              href="/legal/cookies"
              aria-label={t("learnMore")}
              className="underline hover:text-(--fm-accent)"
            >
              {t("learnMore")}
            </Link>
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <button
            type="button"
            onClick={() => decide("rejected")}
            disabled={busy}
            className="rounded border border-(--fm-border) px-3 py-2 text-xs font-medium hover:bg-(--fm-background) disabled:opacity-50"
          >
            {t("reject")}
          </button>
          <button
            type="button"
            onClick={() => decide("necessary")}
            disabled={busy}
            className="rounded border border-(--fm-border) px-3 py-2 text-xs font-medium hover:bg-(--fm-background) disabled:opacity-50"
          >
            {t("necessary")}
          </button>
          <button
            type="button"
            onClick={() => decide("all")}
            disabled={busy}
            className="rounded bg-(--fm-accent) px-4 py-2 text-xs font-semibold text-(--fm-background) hover:opacity-90 disabled:opacity-50"
          >
            {t("acceptAll")}
          </button>
        </div>
      </div>
    </div>
  );
}
